import Vue from 'vue'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { extractCursor } from '@/api/util'

export let perPageItemNum = 20

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  dicom: {
    dicoms: [],
    nextCursor: ''
  },
  importantDicom: {
    dicoms: [],
    nextCursor: ''
  }
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  dicoms: state => state.dicom.dicoms,
  hasDicomNext: state => !!state.dicom.nextCursor,
  importantDicoms: state => {
    return state.importantDicom.dicoms.sort((a, b) => b.id - a.id)
  },
  hasImportantDicomNext: state => !!state.importantDicom.nextCursor
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  updateDicoms: (state, dicoms) => {
    state.dicom.dicoms = dicoms
  },
  addDicoms: (state, dicoms) => {
    state.dicom.dicoms = [...state.dicom.dicoms, ...dicoms]
  },
  updateDicomNext: (state, next) => {
    state.dicom.nextCursor = extractCursor(next)
  },
  updateImportantDicoms: (state, dicoms) => {
    state.importantDicom.dicoms = dicoms
  },
  addImportantDicoms: (state, dicoms) => {
    state.importantDicom.dicoms = [...state.importantDicom.dicoms, ...dicoms]
  },
  updateImportantDicomNext: (state, next) => {
    state.importantDicom.nextCursor = extractCursor(next)
  },
  updateImportantStatus: (state, { isImportant, dicomId }) => {
    const importantDicoms = state.importantDicom.dicoms
    const ids = importantDicoms.map(dicom => dicom.id)
    const dicom = state.dicom.dicoms.find(im => im.id === dicomId)
    const youngestId = ids.sort((a, b) => a - b)[0]
    const next = !!state.importantDicom.nextCursor
    if (dicom) {
      const index = state.dicom.dicoms.indexOf(dicom)
      dicom.isImportant = isImportant
      state.dicom.dicoms.splice(index, 1, dicom)
    }
    if (
      isImportant &&
      (!next ||
        dicomId > youngestId ||
        importantDicoms.length < perPageItemNum) &&
      !ids.includes(dicomId)
    ) {
      const youngerId = ids.find(id => id < dicomId) || 0
      if (dicom)
        state.importantDicom.dicoms.splice(ids.indexOf(youngerId) - 1, 0, dicom)
    }
    if (!isImportant) {
      state.importantDicom.dicoms = importantDicoms.filter(
        dicom => dicom.id !== dicomId
      )
    }
  },
  removeDicom: (state, { dicomId }) => {
    state.dicom.dicoms = state.dicom.dicoms.filter(
      dicom => dicom.id !== dicomId
    )
    state.importantDicom.dicoms = state.importantDicom.dicoms.filter(
      dicom => dicom.id !== dicomId
    )
  }
}
/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  updateDicoms: async ({ commit }, { patientId }) => {
    const res = await Vue.$api.dicom.fetch({ patientId })
    perPageItemNum = res.results.length || perPageItemNum
    commit('updateDicoms', res.results)
    commit('updateDicomNext', res.next || '')
  },
  loadNextDicoms: async ({ commit, state }, { patientId }) => {
    const cursor = state.dicom.nextCursor
    if (!cursor) return
    const res = await Vue.$api.dicom.fetch({ patientId, cursor })
    commit('addDicoms', res.results)
    commit('updateDicomNext', res.next || '')
  },
  updateImportantDicoms: async ({ commit }, { patientId }) => {
    const res = await Vue.$api.dicom.fetch({ patientId, isImportant: true })
    commit('updateImportantDicoms', res.results)
    commit('updateImportantDicomNext', res.next || '')
  },
  loadNextImportantDicoms: async ({ commit, state }, { patientId }) => {
    const cursor = state.importantDicom.nextCursor
    if (!cursor) return
    const res = await Vue.$api.dicom.fetch({
      patientId,
      isImportant: true,
      cursor
    })
    commit('addImportantDicoms', res.results)
    commit('updateImportantDicomNext', res.next || '')
  },
  updateImportantStatus: async (
    { commit },
    { patientId, isImportant, dicomId }
  ) => {
    await Vue.$api.dicom.updateImportant({
      patientId,
      isImportant,
      dicomId
    })
    commit('updateImportantStatus', { isImportant, dicomId })
  },
  removeDicom: async ({ commit }, { patientId, dicomId }) => {
    await Vue.$api.dicom.delete({ patientId, dicomId })
    commit('removeDicom', { dicomId })
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
